import { useCombobox } from "downshift";
import { useId, useMemo, useState } from "react";
import { filtrarOpciones, sinTildes, type OpcionBuscable } from "@/lib/buscar";
import { cn } from "@/lib/cn";
import { LabeledField } from "./LabeledField";

export interface OpcionCombobox extends OpcionBuscable {
  /** Segunda línea en texto suave (ej. la localidad de una dirección). */
  detalle?: string | null;
}

export interface ComboboxProps {
  etiqueta: string;
  opciones: OpcionCombobox[];
  /** Se llama al elegir una opción; el campo se vacía para buscar la siguiente. */
  onElegir: (valor: string) => void;
  placeholder?: string;
  deshabilitado?: boolean;
  className?: string;
  /** Si está, cuando lo escrito no coincide exacto (sin mayúsculas ni tildes) con ninguna opción, ofrece al final
   * "Agregar «texto»"; al elegirla llama a `onCrear(texto recortado)` en vez de `onElegir`. */
  onCrear?: (texto: string) => void;
  /** Texto de la opción de crear (por defecto `Agregar «texto»`). */
  textoCrear?: (texto: string) => string;
  /** Modo asíncrono: las opciones las trae quien lo usa. Se llama con cada cambio de lo escrito; el Combobox no filtra
   * (muestra `opciones` tal cual) y no ofrece crear. */
  onBuscar?: (texto: string) => void;
  /** Mientras llegan las opciones (modo asíncrono): muestra "Buscando…". */
  cargando?: boolean;
  /** Texto sin opciones (por defecto "Ninguna coincide"); `null` no muestra nada. */
  textoVacio?: string | null;
}

const CREAR = "\u0000crear";
const textoCrearPorDefecto = (texto: string) => `Agregar «${texto}»`;

/** Buscador para elegir de una lista larga (Downshift): se escribe, se filtra sin tildes y por palabras, y se elige
 * con mouse o teclado. Es para "agregar de a uno": no guarda un valor elegido. */
export function Combobox({
  etiqueta, opciones, onElegir, placeholder, deshabilitado, className, onCrear, textoCrear = textoCrearPorDefecto, onBuscar, cargando = false,
  textoVacio = "Ninguna coincide",
}: ComboboxProps) {
  const id = useId();
  const [texto, setTexto] = useState("");
  const visibles = useMemo<OpcionCombobox[]>(() => {
    if (onBuscar) return opciones;
    const filtradas = filtrarOpciones(opciones, texto);
    const nuevo = texto.trim();
    if (!onCrear || !nuevo || opciones.some((o) => sinTildes(o.etiqueta.trim()) === sinTildes(nuevo))) return filtradas;
    return [...filtradas, { valor: CREAR, etiqueta: textoCrear(nuevo) }];
  }, [opciones, texto, onCrear, textoCrear, onBuscar]);

  const { isOpen, highlightedIndex, getInputProps, getMenuProps, getItemProps } = useCombobox({
    inputId: id,
    items: visibles,
    itemToString: (o) => o?.etiqueta ?? "",
    inputValue: texto,
    selectedItem: null,
    // Con onCrear, Enter elige la primera (o "Agregar «…»" si no hay coincidencias) en vez de mandar el form.
    defaultHighlightedIndex: onCrear ? 0 : undefined,
    onInputValueChange: ({ inputValue, type }) => {
      if (type !== useCombobox.stateChangeTypes.InputChange) return;
      setTexto(inputValue ?? "");
      onBuscar?.(inputValue ?? "");
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) return;
      const nuevo = texto.trim();
      setTexto("");
      onBuscar?.("");
      if (selectedItem.valor === CREAR) onCrear?.(nuevo);
      else onElegir(selectedItem.valor);
    },
  });

  return (
    <LabeledField etiqueta={etiqueta} htmlFor={id} className={cn("relative", className)}>
      <input
        {...getInputProps({ "aria-labelledby": undefined })}
        placeholder={placeholder}
        disabled={deshabilitado}
        className={cn(
          "h-11 w-full rounded-control border border-borde-fuerte bg-superficie px-3",
          "placeholder:text-texto-suave focus:border-primario focus:outline-none focus:ring-2 focus:ring-primario/25 disabled:bg-superficie-2",
        )}
      />
      <ul
        {...getMenuProps({ "aria-labelledby": undefined, "aria-label": etiqueta })}
        className={cn(
          "absolute top-full z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-control border border-borde bg-superficie p-1 shadow-flotante",
          (!isOpen || (visibles.length === 0 && !cargando && textoVacio === null)) && "hidden",
        )}
      >
        {isOpen &&
          visibles.map((o, i) => (
            <li
              key={o.valor}
              {...getItemProps({ item: o, index: i })}
              className={cn("flex min-h-10 cursor-pointer select-none flex-col justify-center rounded-md px-3 py-2", highlightedIndex === i && "bg-primario-suave")}
            >
              <span>{o.etiqueta}</span>
              {o.detalle && <span className="text-chico text-texto-suave">{o.detalle}</span>}
            </li>
          ))}
        {isOpen && cargando && <li className="px-3 py-2 text-texto-suave">Buscando…</li>}
        {isOpen && !cargando && visibles.length === 0 && textoVacio !== null && <li className="px-3 py-2 text-texto-suave">{textoVacio}</li>}
      </ul>
    </LabeledField>
  );
}
